import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { ReactNode, useEffect, useState } from 'react';
import { FormProvider, Path, useWatch } from 'react-hook-form';
import { SingleValue } from 'react-select';
import {
  loadMedicalBoardOptions,
  loadSpecBoardOptions,
  loadSpecCertOptions,
  loadSubSpecCertOptions,
  loadSubSpecCertOptionsByCertification,
} from '~/api/utils';
import {
  AsyncCreatableSelect,
  CreatableSelect,
  DatePicker,
  FormLayout,
  FormRow as Row,
  FormSection as Section,
  Select,
  TextArea,
  TextInput,
  Toggle,
} from '~/components';
import { Wizard } from '~/components/Wizard/Wizard';
import { WizardStepsProps } from '~/components/Wizard/types';
import { COUNTRIES } from '~/constants';
import { StateOrProvinceInput } from '~/features/vault/facility/StateOrProvinceInput';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { useWizardSteps } from '~/hooks/useWizardSteps';
import { EMPTY_OPTION, EMPTY_OPTION_WITH_ID, TRAINING_TYPES } from '../constants';
import { trainingSchema } from '../schemas';
import { SpecBoardOptions, SpecCertOptions, TrainingFormValues } from '../types';
import { errorHandler, getTrainingCertificationId } from '../utils';

const TAGS = CT.academics.training.trainingForm;

interface FormProps {
  handleClose: () => void;
  trainingForm: Partial<TrainingFormValues>;
  editTraining: (address: TrainingFormValues) => void;
  providerId: number;
  providerCategoryId: number;
}

const VALID_FIELDS: (keyof TrainingFormValues)[] = ['startDate', 'endDate', 'completionDate'];
const STEP_FIELDS: Path<TrainingFormValues>[][] = [
  ['trainingType', 'medicalBoard'],
  ['specialtyBoard', 'specialtyCertification', 'subSpecialtyCertification'],
  [
    'facility',
    'facility.label',
    'facility.address.addressLine1',
    'facility.address.addressLine2',
    'facility.address.city',
    'facility.address.stateCode',
    'facility.address.postalCode',
    'facility.address.countryCode',
  ],
  ['completedFlag', 'startDate', 'endDate', 'completionDate', 'explanationTx'],
];

export const EditTrainingForm = (props: FormProps) => {
  const [specBoardOptions, setSpecBoardOptions] = useState<SpecBoardOptions[]>([]);
  const [specCertOptions, setSpecCertOptions] = useState<SpecCertOptions[]>([]);

  const { editTraining, handleClose, trainingForm, providerId, providerCategoryId } = props;

  const methods = useCustomForm<TrainingFormValues>({
    resolver: zodResolver(trainingSchema),
    defaultValues: trainingForm,
  });
  const { handleSubmit, control, setValue, getValues } = methods;

  const medBoard = useWatch({ control, name: 'medicalBoard' });

  useServerWarnings(methods, trainingForm.warnings, VALID_FIELDS);

  useEffect(() => {
    let active = true;
    setSpecBoardOptions([]);

    if (medBoard?.id) {
      loadSpecBoardOptions(medBoard?.id).then((options) => {
        if (active) setSpecBoardOptions(options);
      });
    }

    return () => {
      active = false;
    };
  }, [medBoard]);

  const specBoard = useWatch({ control, name: 'specialtyBoard' });
  const specialty = useWatch({ control, name: 'specialtyCertification' });
  const specialtyCertificationId = getTrainingCertificationId(specialty)?.toString();
  useEffect(() => {
    let active = true;
    setSpecCertOptions([]);

    if (specBoard?.id) {
      loadSpecCertOptions(specBoard?.id).then((options) => {
        if (active) setSpecCertOptions(options);
      });
    }

    return () => {
      active = false;
    };
  }, [specBoard]);

  const completedFlag = useWatch({ control, name: 'completedFlag' });
  const { currentStep, goBack, goNext, navigationInProgress } = useWizardSteps({
    methods,
    stepFields: STEP_FIELDS,
  });

  useEffect(() => {
    if (!completedFlag) {
      setValue('completionDate', null);
    } else if (completedFlag && getValues('endDate') && !getValues('completionDate')) {
      setValue('completionDate', getValues('endDate'));
    }
  }, [completedFlag]);

  const onSubmitError = (err: unknown) =>
    errorHandler('There was an error submitting the Education form', err);

  const onSubmitSuccess = (values: TrainingFormValues) => {
    editTraining(values);
  };

  const resetSpecialtySelections = () => {
    setValue('specialtyCertification', EMPTY_OPTION_WITH_ID);
    setValue('subSpecialtyCertification', EMPTY_OPTION);
  };

  const handleSpecialtyBoardCreate = (inputValue: string) => {
    setValue('specialtyBoard', { label: inputValue, value: 'userAdded' });
    resetSpecialtySelections();
  };

  const handleSpecialtyCreate = (inputValue: string) => {
    setValue('specialtyCertification', { label: inputValue, value: 'userAdded' });
    setValue('subSpecialtyCertification', EMPTY_OPTION);
  };

  const handleSubSpecialtyCreate = (inputValue: string) => {
    setValue('subSpecialtyCertification', { label: inputValue, value: 'userAdded' });
  };

  const handleMedicalBoardCreate = (inputValue: string) => {
    const medicalBoard = { label: inputValue, value: 'userAdded' };
    setValue('medicalBoard', medicalBoard);
    setValue('specialtyBoard', EMPTY_OPTION_WITH_ID);
    resetSpecialtySelections();
  };

  const wizardSteps: WizardStepsProps[] = [
    {
      header: 'Type & Board',
      label: 'Type',
      cyTag: `${TAGS.form}-type-step`,
      footer: {
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.editBtn}-next-type`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <Select
                label={'Training Type'}
                name={'trainingType'}
                cyTag={TAGS.trainingTypeSelect}
                options={TRAINING_TYPES}
              />
            </Row>
            <Row>
              <AsyncCreatableSelect
                name={'medicalBoard'}
                label={'Medical Board*'}
                cyTag={TAGS.medicalBoardSelect}
                onCreateOption={handleMedicalBoardCreate}
                onChange={(option: SingleValue<{ value: string; label: string }>) => {
                  if (option) {
                    setValue('specialtyBoard', EMPTY_OPTION_WITH_ID);
                    resetSpecialtySelections();
                  }
                }}
                minChars={false}
                loadOptions={() =>
                  providerCategoryId
                    ? loadMedicalBoardOptions(providerId, providerCategoryId)
                    : Promise.resolve([])
                }
                cacheOptions
                defaultOptions
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Specialty',
      label: 'Specialty',
      cyTag: `${TAGS.form}-specialty-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.editBtn}-back-specialty`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.editBtn}-next-specialty`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <CreatableSelect
                name={'specialtyBoard'}
                label={'Specialty Board*'}
                cyTag={TAGS.specBoardSelect}
                options={specBoardOptions}
                onChange={(option) => {
                  if (option) {
                    resetSpecialtySelections();
                  }
                }}
                onCreateOption={handleSpecialtyBoardCreate}
              />
            </Row>
            <Row>
              <CreatableSelect
                name={'specialtyCertification'}
                label={'Specialty Certification*'}
                cyTag={TAGS.specCertSelect}
                options={specCertOptions}
                onChange={() => setValue('subSpecialtyCertification', EMPTY_OPTION)}
                onCreateOption={handleSpecialtyCreate}
              />
            </Row>
            <Row>
              <AsyncCreatableSelect
                key={`${specBoard?.id ?? specBoard?.value ?? 'empty'}:${specBoard?.label ?? ''}:${specialtyCertificationId ?? 'no-parent'}`}
                label={'Subspecialty Certification'}
                name={'subSpecialtyCertification'}
                description={'Enter or select a subspecialty (Optional)'}
                cyTag={TAGS.subSpecCertSelect}
                minChars={false}
                loadOptions={() => {
                  if (specialtyCertificationId) {
                    return loadSubSpecCertOptionsByCertification(specialtyCertificationId);
                  }
                  if (specBoard?.id) {
                    return loadSubSpecCertOptions(specBoard.id);
                  }
                  return Promise.resolve([]);
                }}
                onCreateOption={handleSubSpecialtyCreate}
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Facility',
      label: 'Facility',
      cyTag: `${TAGS.form}-facility-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.editBtn}-back-facility`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.editBtn}-next-facility`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <p>Facility Information</p>
            <Row>
              <TextInput
                name={'facility.label'}
                cyTag={TAGS.facilityNameInput}
                label={'Facility Name*'}
              />
            </Row>
            <Row>
              <TextInput
                label={'Address Line 1*'}
                name={'facility.address.addressLine1'}
                cyTag={TAGS.facilityAddressLine1}
                autoComplete={'street address address-line1'}
              />
            </Row>
            <Row>
              <TextInput
                label={'Address Line 2'}
                name={'facility.address.addressLine2'}
                cyTag={TAGS.facilityAddressLine2}
                autoComplete={'street address address-line2'}
              />
            </Row>
            <Section componentsPerLine={2}>
              <Row>
                <TextInput
                  label={'City*'}
                  name={'facility.address.city'}
                  cyTag={TAGS.facilityAddressCity}
                  autoComplete={'street address city'}
                />
              </Row>
              <Row>
                <StateOrProvinceInput
                  name={'facility.address.stateCode'}
                  countryFieldName={'facility.address.countryCode'}
                  cyTag={TAGS.facilityAddressState}
                  required
                />
              </Row>
              <Row>
                <TextInput
                  label={'Postal Code*'}
                  name={'facility.address.postalCode'}
                  cyTag={TAGS.facilityAddressPostalCode}
                  autoComplete={'postal-code'}
                />
              </Row>
              <Row>
                <Select
                  name={'facility.address.countryCode'}
                  label={'Country*'}
                  cyTag={TAGS.facilityAddressCountry}
                  options={COUNTRIES}
                />
              </Row>
            </Section>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Dates',
      label: 'Dates',
      cyTag: `${TAGS.form}-dates-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.editBtn}-back-dates`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Save',
          cyTag: TAGS.editBtn,
          isSubmit: true,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <Toggle
                name={'completedFlag'}
                cyTag={TAGS.completedToggle}
                label={'Did you complete the training?'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={completedFlag ? 3 : 2}>
            <Row>
              <DatePicker name={'startDate'} cyTag={TAGS.startDate} label={'Start Date'} required />
            </Row>
            <Row>
              <DatePicker name={'endDate'} cyTag={TAGS.endDate} label={'End Date'} />
            </Row>
            {completedFlag && (
              <Row>
                <DatePicker
                  name={'completionDate'}
                  cyTag={TAGS.completionDate}
                  label={'Completion Date'}
                  required
                />
              </Row>
            )}
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea
                name={'explanationTx'}
                cyTag={TAGS.explainTextBox}
                label={'Additional Information'}
                description={'Comments or Description'}
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="training-form"
      >
        <FormLayout>
          <Wizard steps={wizardSteps} currentStep={currentStep} />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
